/**
 * Fallback API: jalur utama tetap https://us-central1-obat-io.cloudfunctions.net/ukai_api,
 * tapi kalau jaringan user ternyata tidak bisa menjangkau domain cloudfunctions.net
 * (diblokir operator, dsb.), request dialihkan ke <origin halaman>/ukai_api
 * (Firebase Hosting rewrite -> function ukai_api yang sama).
 *
 * Cara kerja (memasang pembungkus di window.fetch, jadi semua pemanggil lama ikut tanpa diubah):
 *  1. Request ke .../ukai_api... dikirim ke jalur utama seperti biasa.
 *  2. Kalau fetch gagal karena jaringan (TypeError: "Load failed" / "Failed to fetch"),
 *     kita PROBE dulu: apakah cloudfunctions.net memang tidak terjangkau?
 *       - Probe berhasil  -> penyebabnya bukan blokir domain. Error asli dilempar, request
 *                            TIDAK diulang (menghindari kirim ganda).
 *       - Probe gagal     -> domain tidak terjangkau. Jalur fallback ditandai aktif, lalu
 *                            request yang gagal diulang lewat fallback.
 *  3. Selama tanda aktif (FALLBACK_TTL_MS), semua request ke ukai_api langsung lewat fallback.
 *     Setelah itu jalur utama dicoba lagi.
 *
 * Yang TIDAK tercakup: function selain ukai_api (ukai_apiSuperMentor, main_api, dst) karena
 * rewrite Hosting-nya baru ada untuk ukai_api, dan link unduhan yang dibuka lewat window.open/<a>.
 */

const PRIMARY_ORIGIN = "https://us-central1-obat-io.cloudfunctions.net";
const PRIMARY_PREFIX = `${PRIMARY_ORIGIN}/ukai_api`;
const FALLBACK_PATH = "/ukai_api";

const STORAGE_KEY = "obatukai_api_fallback";
const FALLBACK_TTL_MS = 6 * 60 * 60 * 1000; // 6 jam
const PROBE_TIMEOUT_MS = 8000;

let nativeFetch = null;
let installed = false;
let probeInFlight = null;

// ---------- penyimpanan tanda ----------

function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s && typeof s === "object" ? s : null;
  } catch (e) {
    return null;
  }
}

function writeState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // localStorage tidak tersedia (mode privat ketat, dll): tanda hanya berlaku di memori.
    memoryState = state;
  }
}

let memoryState = null;

/** Mengembalikan { active, since, until, reason, count } untuk ditampilkan/di-log. */
export function getFallbackState() {
  const s = readState() || memoryState;
  const now = Date.now();
  return {
    active: !!(s && s.until && s.until > now),
    since: s?.since || null,
    until: s?.until || null,
    reason: s?.reason || null,
    count: s?.count || 0,
  };
}

export function clearFallback() {
  memoryState = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // abaikan
  }
}

function markFallbackActive(reason) {
  const prev = readState() || memoryState || {};
  const now = Date.now();
  writeState({
    since: prev.until && prev.until > now ? prev.since || now : now,
    until: now + FALLBACK_TTL_MS,
    reason: String(reason || "").slice(0, 200),
    count: (prev.count || 0) + 1,
  });
}

// ---------- util URL ----------

function isPrimaryUkaiUrl(url) {
  if (typeof url !== "string" || !url.startsWith(PRIMARY_PREFIX)) return false;
  const next = url.charAt(PRIMARY_PREFIX.length);
  // cegah salah cocok dengan ukai_apiSuperMentor, ukai_apiMentor, dst.
  return next === "" || next === "/" || next === "?" || next === "#";
}

function toFallbackUrl(url) {
  return `${window.location.origin}${FALLBACK_PATH}${url.slice(PRIMARY_PREFIX.length)}`;
}

function urlOf(input) {
  if (typeof input === "string") return input;
  if (typeof URL !== "undefined" && input instanceof URL) return input.href;
  if (typeof Request !== "undefined" && input instanceof Request) return input.url;
  return null;
}

function canRunHere() {
  // Fallback hanya masuk akal di production yang dilayani lewat https (Firebase Hosting).
  // Di localhost / http tidak ada rewrite /ukai_api.
  const { protocol, hostname } = window.location;
  return protocol === "https:" && hostname !== "localhost" && hostname !== "127.0.0.1";
}

function isAbortError(err, init, input) {
  if (err && err.name === "AbortError") return true;
  if (init && init.signal && init.signal.aborted) return true;
  if (typeof Request !== "undefined" && input instanceof Request && input.signal && input.signal.aborted) return true;
  return false;
}

function isStreamBody(init) {
  return (
    !!init &&
    typeof ReadableStream !== "undefined" &&
    init.body instanceof ReadableStream
  );
}

// ---------- probe ----------

/**
 * true  = cloudfunctions.net TIDAK terjangkau dari jaringan ini.
 * false = terjangkau (respons apa pun, termasuk 404, berarti terjangkau).
 * Probe ke root domain, bukan ke function, supaya tidak memicu eksekusi function.
 */
function primaryIsUnreachable() {
  if (probeInFlight) return probeInFlight;

  probeInFlight = (async () => {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS) : null;
    try {
      await nativeFetch(`${PRIMARY_ORIGIN}/__probe?t=${Date.now()}`, {
        method: "GET",
        mode: "no-cors",
        cache: "no-store",
        credentials: "omit",
        signal: controller ? controller.signal : undefined,
      });
      return false;
    } catch (e) {
      return true;
    } finally {
      if (timer) clearTimeout(timer);
      probeInFlight = null;
    }
  })();

  return probeInFlight;
}

// ---------- pemasangan ----------

export function installApiFallback() {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;

  nativeFetch = window.fetch.bind(window);
  // Dipakai halaman /debug-koneksi untuk tes yang tidak boleh ditutupi fallback.
  window.__nativeFetch = nativeFetch;

  if (!canRunHere()) return;

  window.fetch = async function patchedFetch(input, init) {
    const url = urlOf(input);

    if (
      !url ||
      !isPrimaryUkaiUrl(url) ||
      (init && init.noFallback === true) ||
      isStreamBody(init)
    ) {
      return nativeFetch(input, init);
    }

    const fallbackUrl = toFallbackUrl(url);
    const isRequestObj = typeof Request !== "undefined" && input instanceof Request;
    // Request object hanya bisa dibaca sekali; simpan salinan untuk percobaan ulang.
    const retryInput = isRequestObj ? input.clone() : null;

    const viaFallback = () =>
      isRequestObj ? nativeFetch(new Request(fallbackUrl, retryInput), init) : nativeFetch(fallbackUrl, init);

    // Tanda masih aktif: langsung lewat fallback.
    if (getFallbackState().active) {
      return viaFallback();
    }

    try {
      return await nativeFetch(input, init);
    } catch (err) {
      if (isAbortError(err, init, input) || !(err instanceof TypeError)) throw err;

      const unreachable = await primaryIsUnreachable();
      if (!unreachable) throw err; // bukan blokir domain: jangan ulang request.

      markFallbackActive(`${err.name}: ${err.message}`);
      if (typeof console !== "undefined") {
        console.warn(
          "[apiFallback] cloudfunctions.net tidak terjangkau, API dialihkan ke",
          `${window.location.origin}${FALLBACK_PATH}`
        );
      }
      return viaFallback();
    }
  };
}
